import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FaKey, FaCopy } from 'react-icons/fa';
import { licenseAPI } from '../services/api';

// License key box for the initial setup of an installation, shown before
// anyone signs in (Login and the Application Locked page). It only renders
// while the installation is not licensed (new, expired or wrong server) —
// the backend refuses this activation once a license is active, so a working
// license is changed only by a signed-in Super Admin (Commercial Setup ->
// License). Calls onActivated() once the key is accepted.
const LicenseActivationPanel = ({ onActivated }) => {
  const [status, setStatus] = useState(null);
  const [licenseKey, setLicenseKey] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await licenseAPI.publicStatus();
      const data = res.data?.data || null;
      setStatus(data);
    } catch {
      setStatus(null);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!status || status.licensed) return null;

  const copyId = () => {
    navigator.clipboard?.writeText(status.installationId).then(() => toast.success('Installation ID copied'), () => {});
  };

  const handleActivate = async (e) => {
    e.preventDefault();
    const key = licenseKey.trim();
    if (!key) return;
    setSaving(true);
    try {
      await licenseAPI.activateInitial(key);
      toast.success('License activated. You can sign in now.');
      setLicenseKey('');
      sessionStorage.removeItem('licenseBlockMessage');
      await load();
      onActivated?.();
    } catch (error) {
      toast.error(error.response?.data?.message || 'License key could not be activated');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleActivate} style={{ textAlign: 'left', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: '8px', padding: '14px', margin: '0 0 16px', background: 'var(--bg-color, #f8fafc)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, marginBottom: '6px' }}>
        <FaKey style={{ color: 'var(--primary-color)' }} /> Activate License
      </div>
      <p className="text-muted" style={{ fontSize: 'var(--fs-sm)', margin: '0 0 10px' }}>
        {status.code === 'LICENSE_REQUIRED'
          ? 'This installation is not activated yet. Enter the license key issued for this server to finish setup.'
          : status.message}
      </p>
      <div style={{ fontSize: 'var(--fs-sm)', marginBottom: '10px' }}>
        Installation ID: <strong style={{ fontFamily: 'monospace', letterSpacing: '0.5px' }}>{status.installationId}</strong>
        <button type="button" className="btn btn-sm btn-outline" onClick={copyId} title="Copy Installation ID" style={{ marginLeft: '6px', padding: '2px 6px' }}><FaCopy /></button>
        <div className="text-muted">Send this ID to your provider to get a license key for this server.</div>
      </div>
      <div className="form-group" style={{ marginBottom: '10px' }}>
        <textarea
          className="form-control"
          rows={3}
          placeholder="Paste license key"
          value={licenseKey}
          onChange={(e) => setLicenseKey(e.target.value)}
          style={{ fontFamily: 'monospace', fontSize: 'var(--fs-sm)', resize: 'vertical' }}
          spellCheck={false}
        />
      </div>
      <button type="submit" className="btn btn-primary w-full" disabled={saving || !licenseKey.trim()}>
        {saving ? 'Activating...' : 'Activate License'}
      </button>
    </form>
  );
};

export default LicenseActivationPanel;
